import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Paper,
  Divider,
  CircularProgress,
  Alert
} from "@mui/material";
import {
  X,
  Calendar,
  Image as ImageIcon,
  FileText,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Camera,
  Download,
  MapPin,
  Eye,
  AlertTriangle,
  Trash2,
  Megaphone
} from "lucide-react";
import MaterialTabPanel from "./MaterialTabPanel";
import StatusBadge from "./StatusBadge";
import RejectReasonDialog from "./RejectReasonDialog";
import {
  getPrerequisitePresignedUploadUrl,
  uploadFileToS3,
  createPrerequisitesRequest,
  fetchPrerequisitesByRequestId,
  getFilePresignedViewUrl,
  reviewPrerequisitesRequest,
  deleteDesignerPrerequisiteUpload,
  fetchDesignerEventUploadFiles,
  deleteS3Object
} from "../../../../services/eventUploadsService";
import { downloadFileFromUrl } from "../../../../utils/fileDownloader";

const ManageMaterialsModal = ({
  isOpen,
  onClose,
  event,
  onUpdateEvent,
  onPreviewFile,
  userRole = "designer"
}) => {
  // Files grouped by category: Announcement, Standy, Banner
  const [filesState, setFilesState] = useState({
    announcement: [],
    standy: [],
    banners: []
  });

  // Delete confirmation dialog state
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    isOpen: false,
    category: "",
    file: null
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [designerGeoPhotos, setDesignerGeoPhotos] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Rejection Dialog state for Admin
  const [rejectDialog, setRejectDialog] = useState({
    isOpen: false,
    category: "",
    fileId: null,
    targetTitle: ""
  });

  const instituteName = event?.institute_name || event?.instituteName || "Unknown Institute";
  const domainName = event?.domain_name || event?.domainName || "";
  const eventType = (event?.event_type || event?.type || "FDP").toUpperCase();
  const startDate = event?.start_date || event?.startDate || "";
  const endDate = event?.end_date || event?.endDate || "";
  const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : (event?.dateRange || "—");
  const currentStatus = event?.status ?? event?.overallApprovalStatus;
  const normalizedStatus = String(currentStatus || "").trim().toLowerCase();

  // Determine if event is in read-only state (approved, pending, or past/completed event)
  const isApproved = normalizedStatus === "approved" || normalizedStatus === "accepted";
  const isPending = normalizedStatus === "pending" || normalizedStatus === "pending review";
  const isRejected = normalizedStatus === "rejected" || normalizedStatus === "changes requested";
  const isPast = event?.timelineStatus === "past" || event?.timelineStatus === "completed";
  const isOngoingOrCompleted = event?.timelineStatus === "ongoing" || isPast;
  const isReadOnly = isApproved || isPending || isPast || isOngoingOrCompleted;

  // Filter approved geo photos (fallback or direct API files)
  const approvedGeoPhotos = (event?.geoTaggedPhotos || []).filter(
    (p) => p.approvalStatus === "approved"
  );
  const displayGeoPhotos = designerGeoPhotos.length > 0 ? designerGeoPhotos : approvedGeoPhotos;

  // Fetch existing uploaded files when modal opens
  useEffect(() => {
    if (!isOpen || !event) return;

    setErrorMessage("");
    const eventUploadReqId =
      event?.event_upload_request_id ||
      (isOngoingOrCompleted ? event?.request_id || event?.id : null);

    if (isOngoingOrCompleted && eventUploadReqId) {
      setIsLoadingFiles(true);
      fetchDesignerEventUploadFiles(eventUploadReqId)
        .then(async (res) => {
          const files = res?.files || [];
          const filesWithUrls = await Promise.all(
            files.map(async (f) => {
              try {
                const url = await getFilePresignedViewUrl(f.key_name);
                return {
                  id: f.id,
                  name: f.file_name,
                  url: url,
                  key_name: f.key_name,
                  uploadedAt: f.uploaded_at
                    ? new Date(f.uploaded_at).toLocaleDateString()
                    : "Uploaded",
                  size: "Photo",
                  approvalStatus: "approved"
                };
              } catch {
                return {
                  id: f.id,
                  name: f.file_name,
                  url: null,
                  key_name: f.key_name,
                  uploadedAt: f.uploaded_at
                    ? new Date(f.uploaded_at).toLocaleDateString()
                    : "Uploaded",
                  size: "Photo",
                  approvalStatus: "approved"
                };
              }
            })
          );
          setDesignerGeoPhotos(filesWithUrls);
        })
        .catch((err) => {
          console.error("Failed to fetch designer event upload files:", err);
          setErrorMessage("Failed to load geo-tagged photos.");
        })
        .finally(() => {
          setIsLoadingFiles(false);
        });
    } else {
      const requestId = event?.request_id || event?.id;
      if (requestId) {
        setIsLoadingFiles(true);
        fetchPrerequisitesByRequestId(requestId)
          .then((files) => {
            const categorized = {
              announcement: [],
              standy: [],
              banners: []
            };

            files.forEach((f) => {
              const item = {
                id: f.id,
                name: f.file_name,
                key_name: f.key_name,
                title: f.title,
                uploadedAt: f.uploaded_at
                  ? new Date(f.uploaded_at).toLocaleDateString()
                  : "Uploaded",
                isUploading: false,
                uploadProgress: 100,
                isExisting: true,
                isNew: false,
                url: null // Loaded on-demand when previewed
              };

              const lowerTitle = (f.title || "").toLowerCase();
              if (lowerTitle.includes("announcement")) {
                categorized.announcement.push(item);
              } else if (lowerTitle.includes("standy") || lowerTitle.includes("standee")) {
                categorized.standy.push(item);
              } else {
                categorized.banners.push(item);
              }
            });

            setFilesState(categorized);
          })
          .catch((err) => {
            console.error("Failed to fetch prerequisite files:", err);
            setErrorMessage("Failed to load existing files from server.");
          })
          .finally(() => {
            setIsLoadingFiles(false);
          });
      } else {
        // New booking with no prerequisite request ID yet
        setFilesState({
          announcement: (event.announcement || []).map((a) => ({ ...a, isExisting: true, isNew: false })),
          standy: (event.standy || []).map((s) => ({ ...s, isExisting: true, isNew: false })),
          banners: (event.banners || []).map((b) => ({ ...b, isExisting: true, isNew: false }))
        });
      }
    }
  }, [isOpen, event, isOngoingOrCompleted]);

  // File download helper (fetches presigned URL with download=true / attachment disposition)
  const handleDownloadFile = async (fileUrl, fileName, keyName) => {
    const finalFileName = fileName || "geotag_photo.jpg";
    let targetUrl = null;
    if (keyName) {
      try {
        targetUrl = await getFilePresignedViewUrl(keyName, true, finalFileName);
      } catch (err) {
        console.error("Failed to get presigned view URL for download:", err);
      }
    }
    if (!targetUrl) {
      targetUrl = fileUrl;
    }
    if (!targetUrl) return;

    await downloadFileFromUrl(targetUrl, finalFileName);
  };

  // Download all approved geo photos
  const handleDownloadAllGeoPhotos = async () => {
    for (let i = 0; i < displayGeoPhotos.length; i++) {
      const photo = displayGeoPhotos[i];
      await handleDownloadFile(photo.url, photo.name, photo.key_name);
      if (i < displayGeoPhotos.length - 1) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }
  };

  // Multi-file upload handler using S3 Presigned PUT URLs
  const handleUploadFiles = async (e, category) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    setErrorMessage("");

    // Create temporary entries for all selected files
    const tempEntries = selectedFiles.map((file, idx) => {
      const tempId = `temp-${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 6)}`;
      return {
        id: tempId,
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        type: file.type,
        url: null,
        presigned_url: null,
        isUploading: true,
        uploadProgress: 0,
        uploadedAt: "Uploading...",
        category: category,
        fileObj: file,
        isNew: true,
        isExisting: false
      };
    });

    // Add temporary uploading entries to state
    setFilesState((prev) => ({
      ...prev,
      [category]: [...prev[category], ...tempEntries]
    }));
    e.target.value = "";

    // Process and upload each file directly to S3 via presigned PUT URL
    for (const entry of tempEntries) {
      try {
        // Step 1: Request S3 Presigned URL from backend
        const presignedData = await getPrerequisitePresignedUploadUrl(entry.name);
        const { presigned_url, key_name } = presignedData || {};

        if (!presigned_url) {
          throw new Error("No presigned upload URL returned from server.");
        }

        // Step 2: Upload file binary directly to S3 with progress tracking (PUT request)
        await uploadFileToS3(presigned_url, entry.fileObj, (progress) => {
          setFilesState((prev) => ({
            ...prev,
            [category]: prev[category].map((item) =>
              item.id === entry.id ? { ...item, uploadProgress: progress } : item
            )
          }));
        });

        // Step 3: Fetch GET presigned view URL for S3 key so browser can preview it without 403
        let viewUrl = "";
        if (key_name) {
          try {
            viewUrl = await getFilePresignedViewUrl(key_name);
          } catch (viewErr) {
            console.warn("Could not retrieve presigned view URL immediately:", viewErr);
          }
        }

        // Step 4: Mark file ready with S3 key_name and view URL
        setFilesState((prev) => ({
          ...prev,
          [category]: prev[category].map((item) =>
            item.id === entry.id
              ? {
                  ...item,
                  key_name: key_name || entry.name,
                  url: viewUrl || null,
                  presigned_url: presigned_url,
                  isUploading: false,
                  uploadProgress: 100,
                  uploadedAt: "Just now",
                  title: category === "announcement" ? "Announcement" : category === "standy" ? "Standy" : "Banner",
                  isNew: true,
                  isExisting: false
                }
              : item
          )
        }));
      } catch (uploadErr) {
        console.error(`Error uploading ${entry.name}:`, uploadErr);
        setErrorMessage(`Failed to upload ${entry.name}. Please try again.`);
        // Remove failed item
        setFilesState((prev) => ({
          ...prev,
          [category]: prev[category].filter((item) => item.id !== entry.id)
        }));
      }
    }
  };

  // Preview file (resolves GET presigned view URL and ensures inline display)
  const handlePreviewFile = async (file) => {
    try {
      let targetUrl = file.url;

      // If no view URL yet, fetch GET presigned view URL from backend
      if (!targetUrl && file.key_name) {
        targetUrl = await getFilePresignedViewUrl(file.key_name);
      }

      if (!targetUrl) {
        onPreviewFile(file);
        return;
      }

      const fileName = (file.name || "").toLowerCase();
      const isPdf = fileName.endsWith(".pdf") || file.type === "application/pdf";

      // For PDFs, convert to explicit application/pdf blob to bypass S3 attachment download headers
      if (isPdf && targetUrl.startsWith("http")) {
        try {
          const res = await fetch(targetUrl);
          const rawBlob = await res.blob();
          const pdfBlob = new Blob([rawBlob], { type: "application/pdf" });
          const blobUrl = URL.createObjectURL(pdfBlob);
          onPreviewFile({ ...file, url: blobUrl });
          return;
        } catch (fetchErr) {
          console.warn("Could not convert PDF to inline blob, using direct URL:", fetchErr);
        }
      }

      onPreviewFile({ ...file, url: targetUrl });
    } catch (err) {
      console.error("Failed to get preview URL:", err);
      onPreviewFile(file);
    }
  };

  // Request Remove file handler (Opens Confirmation Dialog)
  const handleRemoveFile = (category, fileId) => {
    const targetFile = filesState[category]?.find((f) => f.id === fileId);
    if (!targetFile) return;
    setDeleteConfirmDialog({
      isOpen: true,
      category,
      file: targetFile
    });
  };

  // Perform Delete after confirmation
  const handleConfirmDeleteFile = async () => {
    const { category, file } = deleteConfirmDialog;
    if (!file) return;

    try {
      setIsDeleting(true);
      // If it's a persisted file in the database, call backend delete API (which handles DB + S3 deletion)
      if (file?.id && typeof file.id === "number") {
        await deleteDesignerPrerequisiteUpload(file.id);
      } else if (file?.key_name) {
        // If it's a newly uploaded file directly on S3 without DB submit yet, delete from S3
        await deleteS3Object(file.key_name);
      }

      setFilesState((prev) => ({
        ...prev,
        [category]: prev[category].filter((f) => f.id !== file.id)
      }));

      setDeleteConfirmDialog({ isOpen: false, category: "", file: null });
    } catch (err) {
      console.error("Error deleting prerequisite upload from backend:", err);
      setErrorMessage(err.message || "Failed to delete file.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Admin Open Reject Dialog
  const handleOpenRejectModal = () => {
    setRejectDialog({
      isOpen: true,
      targetTitle: `${instituteName} Materials`
    });
  };

  // Admin Confirm Reject Request
  const handleConfirmReject = async (feedbackReason) => {
    const targetRequestId = event?.request_id || event?.id;
    if (!targetRequestId) {
      setErrorMessage("No request ID found to reject.");
      return;
    }
    try {
      await reviewPrerequisitesRequest(targetRequestId, {
        action: "rejected",
        reason: feedbackReason
      });
      onUpdateEvent({
        ...event,
        status: "rejected",
        reason: feedbackReason
      });
      onClose();
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || "Failed to reject prerequisite request.");
    }
  };

  // Admin Approve All Materials
  const handleAdminApproveAll = async () => {
    const targetRequestId = event?.request_id || event?.id;
    if (!targetRequestId) {
      setErrorMessage("No request ID found to approve.");
      return;
    }
    try {
      await reviewPrerequisitesRequest(targetRequestId, {
        action: "approved"
      });
      onUpdateEvent({
        ...event,
        status: "approved"
      });
      onClose();
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || "Failed to approve prerequisite request.");
    }
  };

  // Designer Submit & Save All Prerequisites to Backend (ONLY sends newly uploaded files)
  const handleDesignerSubmit = async () => {
    // Check if any file is still uploading
    const isAnyUploading = ["announcement", "standy", "banners"].some((cat) =>
      filesState[cat]?.some((f) => f.isUploading)
    );
    if (isAnyUploading) {
      setErrorMessage("Please wait for all files to finish uploading.");
      return;
    }

    // Collect ONLY newly uploaded files with key_name to prevent sending existing files in payload
    const newFiles = [
      ...(filesState.announcement || []).filter((f) => f.isNew && f.isUploading !== true),
      ...(filesState.standy || []).filter((f) => f.isNew && f.isUploading !== true),
      ...(filesState.banners || []).filter((f) => f.isNew && f.isUploading !== true)
    ].map((f) => ({
      title: f.title || (f.category === "announcement" ? "Announcement" : f.category === "standy" ? "Standy" : "Banner"),
      file_name: f.name,
      key_name: f.key_name
    })).filter((f) => Boolean(f.key_name));

    const totalFilesCount =
      (filesState.announcement?.length || 0) +
      (filesState.standy?.length || 0) +
      (filesState.banners?.length || 0);

    if (totalFilesCount === 0) {
      setErrorMessage("Please upload at least one file before submitting.");
      return;
    }

    const bookslotId = event?.bookslot_id || event?.bookslotId || event?.id;
    if (!bookslotId) {
      setErrorMessage("Missing bookslot ID for this event.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const res = await createPrerequisitesRequest({
        bookslot_id: Number(bookslotId),
        files: newFiles
      });

      onUpdateEvent({
        ...event,
        id: res.request_id || event.id,
        status: "pending"
      });
      onClose();
    } catch (err) {
      console.error("Failed to create prerequisite request:", err);
      setErrorMessage(
        err.response?.data?.detail ||
        err.message ||
        "Failed to submit prerequisites. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!event) return null;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "14px",
            overflow: "hidden",
            maxHeight: "92vh"
          }
        }}
      >
        {/* Modal Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: "1px solid #e2e8f0",
            py: 1.75,
            px: 3,
            backgroundColor: "#f8fafc"
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.5, flexWrap: "wrap" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.05rem" }}
              >
                {instituteName}
              </Typography>
              <Chip
                label={eventType}
                size="small"
                sx={{
                  height: "20px",
                  fontSize: "0.6875rem",
                  fontWeight: 700,
                  backgroundColor: eventType === "FDP" ? "#eff6ff" : "#f0fdf4",
                  color: eventType === "FDP" ? "#1877f2" : "#16a34a",
                  border: `1px solid ${
                    eventType === "FDP" ? "#bfdbfe" : "#bbf7d0"
                  }`,
                  borderRadius: "4px"
                }}
              />
              <StatusBadge status={currentStatus} />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: "#64748b",
                fontSize: "0.78rem"
              }}
            >
              <Calendar size={13} color="#1877f2" />
              <span>{dateRange}</span>
              {domainName && (
                <>
                  <span>•</span>
                  <span style={{ fontWeight: 600, color: "#475569" }}>{domainName}</span>
                </>
              )}
            </Box>
          </Box>

          <IconButton onClick={onClose} size="small" sx={{ color: "#94a3b8" }}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        {/* Modal Body: Sequential Sections */}
        <DialogContent
          dividers
          sx={{
            p: 3,
            pt: "20px !important",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#cbd5e1",
              borderRadius: "4px"
            }
          }}
        >
          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage("")}
              sx={{ borderRadius: "8px", fontSize: "0.8rem", py: 0.5 }}
            >
              {errorMessage}
            </Alert>
          )}

          {isLoadingFiles ? (
            <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={28} sx={{ color: "#1877f2" }} />
              <Typography sx={{ fontSize: "0.82rem", color: "#64748b", fontWeight: 500 }}>
                Loading prerequisite files...
              </Typography>
            </Box>
          ) : isOngoingOrCompleted ? (
            /* Ongoing & Completed Events: Only show Geo-Tagged Event Photos */
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.95rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1
                  }}
                >
                  <Camera size={18} color="#1877f2" />
                  Geo-Tagged Event Photos
                </Typography>

                {displayGeoPhotos.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Download size={13} />}
                    onClick={handleDownloadAllGeoPhotos}
                    sx={{
                      fontSize: "0.74rem",
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: "6px",
                      py: 0.35,
                      px: 1.25,
                      color: "#1877f2",
                      borderColor: "#bfdbfe",
                      backgroundColor: "#eff6ff",
                      "&:hover": { backgroundColor: "#dbeafe" }
                    }}
                  >
                    Download All ({displayGeoPhotos.length})
                  </Button>
                )}
              </Box>

              {displayGeoPhotos.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(auto-fill, minmax(240px, 1fr))" },
                    gap: 1.5
                  }}
                >
                  {displayGeoPhotos.map((photo) => (
                    <Paper
                      key={photo.id}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        backgroundColor: "#ffffff",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#cbd5e1", boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }
                      }}
                    >
                      {/* Thumbnail & GPS badge */}
                      <Box sx={{ position: "relative", mb: 1.25 }}>
                        <Box
                          onClick={() => handlePreviewFile(photo)}
                          sx={{
                            width: "100%",
                            height: 130,
                            borderRadius: "6px",
                            overflow: "hidden",
                            backgroundColor: "#e2e8f0",
                            cursor: "pointer",
                            border: "1px solid #cbd5e1"
                          }}
                        >
                          <img
                            src={photo.url}
                            alt={photo.name}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        </Box>
                        {photo.geoCoordinates && (
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 6,
                              left: 6,
                              backgroundColor: "rgba(15, 23, 42, 0.75)",
                              backdropFilter: "blur(4px)",
                              color: "#ffffff",
                              px: 0.75,
                              py: 0.2,
                              borderRadius: "4px",
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.4
                            }}
                          >
                            <MapPin size={10} color="#38bdf8" />
                            {photo.geoCoordinates}
                          </Box>
                        )}
                      </Box>

                      {/* Name & Coordinator Approval Info */}
                      <Box sx={{ mb: 1.25 }}>
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            color: "#0f172a",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}
                        >
                          {photo.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#16a34a",
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.25
                          }}
                        >
                          <CheckCircle2 size={11} color="#16a34a" />
                          Approved by Coordinator
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.67rem", display: "block" }}>
                          {photo.size} • {photo.uploadedAt}
                        </Typography>
                      </Box>

                      {/* Action buttons: Preview & Download */}
                      <Box sx={{ display: "flex", gap: 1, pt: 1, borderTop: "1px solid #f1f5f9" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Eye size={12} />}
                          onClick={() => handlePreviewFile(photo)}
                          sx={{
                            flex: 1,
                            py: 0.3,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            textTransform: "none",
                            color: "#475569",
                            borderColor: "#cbd5e1",
                            "&:hover": { backgroundColor: "#f8fafc" }
                          }}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Download size={12} />}
                          onClick={() => handleDownloadFile(photo.url, photo.name, photo.key_name)}
                          sx={{
                            flex: 1,
                            py: 0.3,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            borderRadius: "6px",
                            textTransform: "none",
                            backgroundColor: "#1877f2",
                            "&:hover": { backgroundColor: "#1465cc" },
                            boxShadow: "none"
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    textAlign: "center",
                    borderRadius: "8px",
                    backgroundColor: "#f8fafc",
                    border: "1px dashed #cbd5e1"
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.8rem", fontStyle: "italic" }}>
                    No geo-tagged photos uploaded for this event yet.
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : (
            /* Upcoming Events: Show Upload Sections (Announcement, Standy, Banner) */
            <>
              {/* Admin Feedback Warning Banner if Rejected */}
              {isRejected && event.reason && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: "8px",
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca"
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#991b1b", fontWeight: 700, display: "block" }}
                  >
                    Feedback Note from Admin:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#7f1d1d", fontSize: "0.8rem", mt: 0.25 }}
                  >
                    {event.reason}
                  </Typography>
                </Paper>
              )}

              {/* Section 1: Announcement */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.92rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1
                  }}
                >
                  <Megaphone size={16} color="#1877f2" />
                  Announcement
                </Typography>
                <MaterialTabPanel
                  category="announcement"
                  title="Announcement"
                  formatHint="JPG, PNG, PDF (Max 10MB)"
                  acceptFormats="image/png, image/jpeg, image/jpg, application/pdf"
                  files={filesState.announcement || []}
                  onUpload={handleUploadFiles}
                  onRemove={handleRemoveFile}
                  onPreview={handlePreviewFile}
                  userRole={userRole}
                  isReadOnly={isReadOnly}
                />
              </Box>

              <Divider sx={{ borderColor: "#e2e8f0" }} />

              {/* Section 2: Standy */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.92rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1
                  }}
                >
                  <ImageIcon size={16} color="#1877f2" />
                  Standy
                </Typography>
                <MaterialTabPanel
                  category="standy"
                  title="Standy"
                  formatHint="JPG, PNG, PDF (Max 10MB)"
                  acceptFormats="image/png, image/jpeg, image/jpg, application/pdf"
                  files={filesState.standy || []}
                  onUpload={handleUploadFiles}
                  onRemove={handleRemoveFile}
                  onPreview={handlePreviewFile}
                  userRole={userRole}
                  isReadOnly={isReadOnly}
                />
              </Box>

              <Divider sx={{ borderColor: "#e2e8f0" }} />

              {/* Section 3: Banner */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: "#0f172a",
                    fontSize: "0.92rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1
                  }}
                >
                  <ImageIcon size={16} color="#1877f2" />
                  Banner
                </Typography>
                <MaterialTabPanel
                  category="banners"
                  title="Banner"
                  formatHint="JPG, PNG, PDF (Max 10MB)"
                  acceptFormats="image/png, image/jpeg, image/jpg, application/pdf"
                  files={filesState.banners || []}
                  onUpload={handleUploadFiles}
                  onRemove={handleRemoveFile}
                  onPreview={handlePreviewFile}
                  userRole={userRole}
                  isReadOnly={isReadOnly}
                />
              </Box>
            </>
          )}
        </DialogContent>

        {/* Modal Footer Actions */}
        <DialogActions
          sx={{
            p: 2,
            px: 3,
            borderTop: "1px solid #e2e8f0",
            justifyContent: "space-between",
            backgroundColor: "#f8fafc"
          }}
        >
          <Button
            onClick={onClose}
            size="small"
            sx={{ textTransform: "none", fontSize: "0.8rem", color: "#64748b" }}
          >
            Close
          </Button>

          {/* Actions for Designer Role: Only show Save & Submit when NOT pending, NOT approved, and NOT past */}
          {userRole !== "admin" && !isReadOnly && (
            <Button
              variant="contained"
              size="small"
              disabled={isSubmitting}
              onClick={handleDesignerSubmit}
              endIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ArrowRight size={15} />
                )
              }
              sx={{
                px: 2.5,
                py: 0.6,
                borderRadius: "6px",
                fontWeight: 700,
                fontSize: "0.8125rem",
                textTransform: "none",
                backgroundColor: "#1877f2",
                "&:hover": {
                  backgroundColor: "#1465cc"
                }
              }}
            >
              {isSubmitting ? "Submitting..." : "Save & Submit Files"}
            </Button>
          )}

          {/* Actions for Admin Role: Show Reject & Accept Material when request is pending review */}
          {userRole === "admin" && isPending && (
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                startIcon={<XCircle size={15} />}
                onClick={handleOpenRejectModal}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8125rem",
                  borderRadius: "6px",
                  borderColor: "#fca5a5",
                  backgroundColor: "#fef2f2",
                  "&:hover": {
                    backgroundColor: "#fee2e2",
                    borderColor: "#ef4444"
                  }
                }}
              >
                Reject Request
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<CheckCircle2 size={15} />}
                onClick={handleAdminApproveAll}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8125rem",
                  borderRadius: "6px",
                  px: 2.5,
                  backgroundColor: "#16a34a",
                  "&:hover": {
                    backgroundColor: "#15803d"
                  }
                }}
              >
                Accept Material
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Reason Dialog */}
      <RejectReasonDialog
        isOpen={rejectDialog.isOpen}
        onClose={() =>
          setRejectDialog({ isOpen: false, category: "", fileId: null, targetTitle: "" })
        }
        targetItemTitle={rejectDialog.targetTitle}
        onConfirmReject={handleConfirmReject}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.isOpen}
        onClose={() =>
          !isDeleting &&
          setDeleteConfirmDialog({ isOpen: false, category: "", file: null })
        }
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <AlertTriangle color="#ef4444" size={24} />
          <Typography variant="h6" fontWeight={700} fontSize="1rem">
            Delete File
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
            Are you sure you want to permanently delete{" "}
            <b>{deleteConfirmDialog.file?.name || "this file"}</b>?
            This action cannot be undone and will delete the file from the server.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setDeleteConfirmDialog({ isOpen: false, category: "", file: null })}
            disabled={isDeleting}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleConfirmDeleteFile}
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Trash2 size={16} />
              )
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              backgroundColor: "#ef4444",
              "&:hover": { backgroundColor: "#dc2626" }
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManageMaterialsModal;
